import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { VARIABLE_SIMULATION_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';

/**
 * variable_simulation — one slider (or stepped values) drives a simple SVG readout.
 *
 * Reuse: orbit “feel” vs distance; gear size vs speed; rows/columns vs total; heat vs evaporation (author labels in JSON).
 *
 * activityData.content schema:
 * {
 *   instructions?: string,
 *   variable: {
 *     id?: string,
 *     label: string,
 *     min: number,
 *     max: number,
 *     step?: number   // default 1
 *   },
 *   marks: [
 *     {
 *       value: number,
 *       caption: string,
 *       readout?: string,
 *       visual?: {
 *         orbitRadius?: number,   // px, moon distance from planet center
 *         moonAngleDeg?: number   // optional static angle for moon dot
 *       }
 *     }
 *   ],
 *   checkPrompt?: string,
 *   target?: { value: number },   // optional: learner taps Check when slider matches
 *   victory?: { message?: string }
 * }
 *
 * Visual: central “planet”, moon on a ring whose radius comes from linear interpolation between marks bracketing the current value.
 */

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function interpMarks(marks, value) {
  const sorted = [...marks].sort((x, y) => x.value - y.value);
  if (sorted.length === 0) return { caption: '', readout: '', orbitRadius: 90, moonAngleDeg: 0 };
  if (value <= sorted[0].value) return sorted[0];
  if (value >= sorted[sorted.length - 1].value) return sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (value >= lo.value && value <= hi.value) {
      const t = (value - lo.value) / (hi.value - lo.value || 1);
      const r0 = lo.visual?.orbitRadius ?? 70;
      const r1 = hi.visual?.orbitRadius ?? 100;
      const a0 = lo.visual?.moonAngleDeg ?? 40;
      const a1 = hi.visual?.moonAngleDeg ?? 40;
      const readoutLo = lo.readout != null ? String(lo.readout) : lo.caption;
      const readoutHi = hi.readout != null ? String(hi.readout) : hi.caption;
      return {
        caption: t < 0.5 ? lo.caption : hi.caption,
        readout: t < 0.5 ? readoutLo : readoutHi,
        visual: {
          orbitRadius: lerp(r0, r1, t),
          moonAngleDeg: lerp(a0, a1, t),
        },
      };
    }
  }
  return sorted[0];
}

export default function VariableSimulationEngine({
  title = 'Variable simulation',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_variable_simulation',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const readableLessonInstruction =
    typeof guidedContext?.readableInstruction === 'string' ? guidedContext.readableInstruction.trim() : '';
  const authoredInstructions =
    typeof content.instructions === 'string' && content.instructions.trim() ? content.instructions.trim() : '';

  const variable = content.variable && typeof content.variable === 'object' ? content.variable : {};
  const min = Number(variable.min);
  const max = Number(variable.max);
  const step = Number(variable.step) > 0 ? Number(variable.step) : 1;
  const marks = Array.isArray(content.marks) ? content.marks : [];
  const target = content.target && typeof content.target === 'object' ? content.target : null;
  const targetValue = target != null ? Number(target.value) : null;

  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : 100;
  const startVal = marks.length ? Number(marks[0].value) || safeMin : safeMin;

  const [value, setValue] = useState(startVal);
  const [completed, setCompleted] = useState(false);
  const [wrong, setWrong] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');

  const interp = useMemo(() => interpMarks(marks, value), [marks, value]);

  const orbitR = interp.visual?.orbitRadius ?? 88;
  const angleDeg = interp.visual?.moonAngleDeg ?? 35;
  const rad = (angleDeg * Math.PI) / 180;
  const cx = 100;
  const cy = 100;
  const mx = cx + orbitR * Math.cos(rad);
  const my = cy + orbitR * Math.sin(rad);

  const tapCompact = singleSurface && isTapFirstLessonUi(lessonUiMode);
  const shellLesson = typeof onRegisterLessonFooterActions === 'function';
  /** Guided lesson shell: Check + optional show-answer live in the lesson footer (saves vertical space on phones). */
  const shellDeferCheck =
    shellLesson && targetValue != null && Number.isFinite(targetValue);

  /** Step header already carries the task; omit the activity card title while the footer owns Check. */
  const hideCardTitle = shellDeferCheck && singleSurface;

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: VARIABLE_SIMULATION_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer } = guided;

  const readoutText =
    typeof interp.readout === 'string' && interp.readout.trim()
      ? interp.readout
      : String(interp.caption || '');

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    if (targetValue != null && Number.isFinite(targetValue)) {
      setValue(targetValue);
      setWrong(false);
    }
  }, [completed, noteUserAction, targetValue]);

  const commitCheck = useCallback(() => {
    if (completed) return;
    noteUserAction();
    if (targetValue != null && Number.isFinite(targetValue)) {
      if (Math.abs(value - targetValue) < step / 2 + 0.0001) {
        setCompleted(true);
        noteCorrectAnswer();
        celebrate();
        setA11y('Matched the target.');
        trackMiniAppEvent.completed(activityId);
        onComplete?.({ activityId, engine: 'variable_simulation', perfect: true });
      } else {
        setWrong(true);
        noteIncorrectAnswer();
        setA11y('Not quite — adjust the slider.');
        trackMiniAppEvent.interaction(activityId, 'incorrect');
      }
    } else {
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('Exploration complete.');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'variable_simulation', perfect: true });
    }
  }, [
    activityId,
    celebrate,
    completed,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    noteUserAction,
    onComplete,
    step,
    targetValue,
    value,
  ]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (!shellLesson || completed) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer =
      shellDeferCheck && Array.isArray(hints) && hints.length > 0 ? revealAnswer : undefined;
    onRegisterLessonFooterActions({
      checkAnswer: commitCheck,
      checkAnswerDisabled: false,
      ...(shellDeferCheck ? {} : { checkAnswerLabel: 'Done exploring' }),
      ...(showAnswer ? { showAnswer } : {}),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    commitCheck,
    completed,
    hints,
    onRegisterLessonFooterActions,
    revealAnswer,
    shellDeferCheck,
    shellLesson,
  ]);

  const onSlide = (e) => {
    if (completed) return;
    noteUserAction();
    setWrong(false);
    const v = Number(e.target.value);
    setValue(v);
    onProgress?.({ activityId, engine: 'variable_simulation', value: v });
  };

  /** Lesson shell omits the duplicate goal card for this engine; keep one prompt line here. */
  const instructionBody = readableLessonInstruction || authoredInstructions;

  const rootStyle = {
    ...getEngineRootStyle({ singleSurface }),
    position: 'relative',
    ...(guided.contentReserveStyle || {}),
  };

  const titleStyleUse = tapCompact ? { ...titleStyle, margin: '0 0 6px', fontSize: 'clamp(1rem, 3vw, 1.18rem)' } : titleStyle;
  const instructStyleUse = tapCompact
    ? { ...instructionsStyle, fontSize: 14, lineHeight: 1.35, marginBottom: 8 }
    : instructionsStyle;
  const captionStyle = tapCompact
    ? { ...instructStyleUse, marginTop: 0, marginBottom: 4, textAlign: 'center', fontWeight: 700 }
    : { ...instructStyleUse, fontWeight: 700, marginTop: 0, textAlign: 'center' };

  const orbitWrapStyle = {
    textAlign: 'center',
    marginBottom: tapCompact ? 6 : 12,
    maxHeight: tapCompact ? 'min(152px, 30vh)' : undefined,
    lineHeight: 0,
    overflow: 'hidden',
    display: tapCompact ? 'flex' : 'block',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const svgDims = tapCompact ? { width: '100%', maxWidth: 220 } : { width: '100%', maxWidth: 280 };

  return (
    <div style={rootStyle}>
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Great exploring!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!hideCardTitle ? <h2 style={titleStyleUse}>{title}</h2> : null}

      {instructionBody ? <p style={instructStyleUse}>{instructionBody}</p> : null}

      <div style={orbitWrapStyle}>
        <svg
          viewBox="0 0 200 200"
          {...svgDims}
          style={{ aspectRatio: '1 / 1', height: 'auto', verticalAlign: 'middle', borderRadius: 14 }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id="planetGrad" cx="40%" cy="35%" r="65%">
              <stop offset="0%" stopColor="#93c5fd" />
              <stop offset="55%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="200" height="200" fill="rgba(15,23,42,0.06)" rx="12" />
          <circle
            cx={cx}
            cy={cy}
            r={orbitR}
            fill="none"
            stroke="rgba(99,102,241,0.35)"
            strokeWidth="2"
            strokeDasharray="6 6"
          />
          <circle cx={cx} cy={cy} r="22" fill="url(#planetGrad)" stroke="#1e40af" strokeWidth="2" />
          <circle cx={mx} cy={my} r="8" fill="#e2e8f0" stroke="#64748b" strokeWidth="1.5" />
        </svg>
      </div>

      {tapCompact ? (
        <p style={{ ...captionStyle, marginBottom: 6 }}>
          <span>{interp.caption}</span>
          <span style={{ opacity: 0.45, padding: '0 6px' }}>·</span>
          <span style={{ fontWeight: 800, color: 'var(--color-primary)' }}>{readoutText}</span>
        </p>
      ) : (
        <>
          <p style={captionStyle}>{interp.caption}</p>
          <p
            style={{
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--color-primary)',
              marginBottom: 12,
            }}
          >
            {readoutText}
          </p>
        </>
      )}

      <label
        style={{
          display: 'block',
          fontWeight: 700,
          marginBottom: tapCompact ? 6 : 8,
          fontSize: tapCompact ? 13 : 15,
        }}
      >
        {variable.label || 'Variable'}
        <input
          type="range"
          min={safeMin}
          max={safeMax}
          step={step}
          value={value}
          disabled={completed}
          onChange={onSlide}
          style={{
            width: '100%',
            marginTop: tapCompact ? 4 : 8,
          }}
        />
      </label>

      {targetValue != null && Number.isFinite(targetValue) ? (() => {
        const line = tapCompact
          ? typeof content.checkPrompt === 'string'
            ? content.checkPrompt.trim()
            : ''
          : (typeof content.checkPrompt === 'string' && content.checkPrompt.trim()) ||
            `Slide until you match the mission target (${targetValue}).`;
        return line ? (
          <p
            style={{
              fontSize: tapCompact ? 12 : 14,
              opacity: 0.85,
              marginBottom: tapCompact ? 6 : 10,
              lineHeight: 1.35,
            }}
          >
            {line}
          </p>
        ) : null;
      })() : null}

      {wrong && (
        <p style={{ color: '#b91c1c', fontWeight: 600, marginBottom: tapCompact ? 6 : 10, fontSize: tapCompact ? 13 : 15 }}>
          Try another value, then check again.
        </p>
      )}

      {!shellLesson ? (
        <button type="button" style={primaryBtn} onClick={commitCheck} disabled={completed}>
          {targetValue != null ? 'Check' : 'Done exploring'}
        </button>
      ) : null}
    </div>
  );
}
